import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  DialogContentText
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';

const statusColors = {
  en_attente: 'warning',
  acceptee: 'success',
  refusee: 'error',
  en_cours: 'info'
};

const statusLabels = {
  en_attente: 'En attente',
  acceptee: 'Acceptée',
  refusee: 'Refusée',
  en_cours: "En cours d'étude"
};

function Candidatures() {
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [postulationDialog, setPostulationDialog] = useState({ open: false });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchCandidatures = async () => {
      try {
        setLoading(true);
        // D'abord, récupérer l'ID de l'étudiant connecté
        const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/me`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (!userResponse.ok) {
          throw new Error('Erreur lors de la récupération des informations utilisateur');
        }

        const userData = await userResponse.json();
        // Récupérer l'ID étudiant depuis la nouvelle structure
        const etudiantId = userData.user.etudiant.id_etudiants;

        // Utiliser l'ID dans la route
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${etudiantId}/candidatures`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des candidatures');
        }

        const data = await response.json();
        setCandidatures(data.candidatures || []);
        setError(null);
      } catch (error) {
        console.error("Erreur lors du chargement des candidatures:", error);
        setError("Une erreur est survenue lors du chargement de vos candidatures");
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchCandidatures();
    }
  }, [currentUser]);

  const handleCardClick = (offreId) => {
    navigate(`/offres/${offreId}`, { 
      state: { 
        from: '/candidatures',
        page: currentPage 
      } 
    });
  };

  const handleOpenFile = (url) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        align="center"
        sx={{
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(20px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' }
          }
        }}
      >
        Mes Candidatures
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {candidatures.length === 0 ? (
        <Typography 
          align="center"
          sx={{
            opacity: 0,
            animation: 'fadeIn 0.8s ease-out 0.3s forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          Vous n'avez pas encore postulé à des offres
        </Typography>
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {candidatures.map((candidature, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              key={candidature.id_candidature}
              sx={{ 
                maxWidth: '400px',
                opacity: 0,
                animation: `fadeIn 0.8s ease-out ${0.2 + index * 0.1}s forwards`,
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {candidature.offre?.titre || 'Sans titre'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {candidature.offre?.entreprise?.nom || 'Entreprise non spécifiée'}
                    </Typography>

                    <Box sx={{ my: 2 }}>
                      <Chip 
                        label={statusLabels[candidature.statut] || 'Statut inconnu'} 
                        color={statusColors[candidature.statut] || 'default'}
                        sx={{ mr: 1 }}
                      />
                      <Chip 
                        label={`Postulé le ${new Date(candidature.date_postulation).toLocaleDateString('fr-FR')}`}
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type : {candidature.offre?.type_stage || 'Stage'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Durée : {candidature.offre?.duree || 'Non spécifiée'}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        startIcon={<PictureAsPdfIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFile(candidature.cv_path);
                        }}
                        variant="outlined"
                        sx={{ flex: 1 }}
                      >
                        CV
                      </Button>
                      
                      <Button
                        startIcon={<PictureAsPdfIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenFile(candidature.lettre_motivation);
                        }}
                        variant="outlined"
                        sx={{ flex: 1 }}
                      >
                        LM
                      </Button>
                    </Box>

                    <Button
                      variant="contained"
                      onClick={() => handleCardClick(candidature.offre?.id_offre)}
                      fullWidth
                    >
                      Voir l'offre
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog de postulation */}
      <Dialog
        open={postulationDialog.open}
        onClose={() => setPostulationDialog({ ...postulationDialog, open: false })}
      >
        <DialogTitle>
          Postuler à l'offre
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pour postuler, veuillez fournir votre CV et une lettre de motivation au format PDF.
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Les documents doivent faire une seule page chacun et ne pas dépasser 5 Mo.
            </Typography>
          </DialogContentText>
          
          {/* ... reste du code du Dialog ... */}
        </DialogContent>
        {/* ... reste du code du Dialog ... */}
      </Dialog>
    </Container>
  );
}

export default Candidatures; 